/**
 * PailletteFace — hex grid of metallic paillette discs (canvas-rendered).
 *
 * A single <canvas> element per instance replaces thousands of DOM nodes.
 * Each disc glints by cycling opacity. Some discs bloom into a 4-pointed
 * cross-star at peak brightness — the star rotates as it fades.
 *
 * "Foreign glints" add occasional off-palette colors for realism:
 * cool silver among warm golds, warm gold among cool creams, etc.
 */

import { memo, useEffect, useRef, useMemo } from 'react';

// ─── Defaults ───────────────────────────────────────────
const DEFAULT_BASE = '#f7e5c5';  // theme cream

// ─── Disc layout ────────────────────────────────────────
const DISC_R = 1.5;              // disc radius (px)
const STEP = 8;                  // center-to-center spacing
const ROW_H = STEP * 0.866;     // hex row height (sin 60°)

// ─── Animation ──────────────────────────────────────────
const DUR_MIN = 2.6;
const DUR_MAX = 11.7;
const FLASH_DUR_MIN = 1.3;
const FLASH_DUR_MAX = 4.2;
const DENSITY = 0.36;
const FLASH_RATIO = 0.10;

// ─── Sparkle (cross-star bloom at peak brightness) ──────
// ~2% of discs bloom into a cross-star when they reach peak brightness.
// The star is phase-locked to the disc: it appears at peak, rotates, fades.
const SPARKLE_RATIO = 0.008;
const SPARKLE_HALF = 10;        // half-size of sparkle star (px)

// Sparkle timing relative to disc cycle (disc peaks at t≈0.80):
//   0.72–0.78: star scales up + fades in
//   0.78–0.86: star holds at full, slow rotate
//   0.86–0.96: star shrinks + fades out, faster rotate
const SP_IN_START = 0.72;
const SP_IN_END   = 0.78;
const SP_HOLD_END = 0.86;
const SP_OUT_END  = 0.96;
const SP_ROTATE_TOTAL = 40;     // degrees of rotation across full sparkle life

// ─── Foreign glint ratio ────────────────────────────────
const FOREIGN_RATIO = 0.02;

// ─── Pre-computed foreign glint palettes ────────────────
// Cool palette: silver, platinum, blue-silver (used on warm bases)
const COOL_GLINTS = [
  { normal: 'rgb(190,195,210)', flash: 'rgb(220,224,235)', sparkle: [238, 240, 248] },
  { normal: 'rgb(175,182,205)', flash: 'rgb(210,216,232)', sparkle: [232, 236, 246] },
  { normal: 'rgb(200,198,218)', flash: 'rgb(228,226,240)', sparkle: [242, 241, 250] },
];
// Warm palette: gold, rose-gold, champagne (used on cool/neutral bases)
const WARM_GLINTS = [
  { normal: 'rgb(210,175,80)',  flash: 'rgb(235,210,140)', sparkle: [248, 235, 190] },
  { normal: 'rgb(200,165,120)', flash: 'rgb(228,205,168)', sparkle: [244, 232, 210] },
  { normal: 'rgb(215,185,130)', flash: 'rgb(238,220,180)', sparkle: [248, 240, 220] },
];

// ─── Sparkle star vertices (4-pointed cross) ────────────
const STAR_PTS: [number, number][] = [
  [0, -1], [0.04, -0.16], [1, 0], [0.04, 0.16],
  [0, 1], [-0.04, 0.16], [-1, 0], [-0.04, -0.16],
];

// ─── Color math ─────────────────────────────────────────
function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function lightenRGB(rgb: [number, number, number], amount: number): [number, number, number] {
  return [
    Math.round(rgb[0] + (255 - rgb[0]) * amount),
    Math.round(rgb[1] + (255 - rgb[1]) * amount),
    Math.round(rgb[2] + (255 - rgb[2]) * amount),
  ];
}

// ─── Keyframe math ──────────────────────────────────────
// Disc cycle: 0–60% invisible, 60–80% fade in, 80–100% fade out
function glintOpacity(t: number): number {
  if (t < 0.6) return 0;
  if (t < 0.8) return (t - 0.6) * 5;   // 0→1 over 20%
  return (1 - t) * 5;                    // 1→0 over 20%
}

// Phase-locked sparkle: appears during disc peak, rotates as it fades
function sparkleParams(t: number): { opacity: number; scale: number; rotDeg: number } {
  if (t < SP_IN_START || t >= SP_OUT_END) return { opacity: 0, scale: 0, rotDeg: 0 };

  const lifeT = (t - SP_IN_START) / (SP_OUT_END - SP_IN_START); // 0→1 across sparkle life
  const rotDeg = lifeT * SP_ROTATE_TOTAL;

  if (t < SP_IN_END) {
    // Scale up + fade in
    const p = (t - SP_IN_START) / (SP_IN_END - SP_IN_START);
    return { opacity: p, scale: p, rotDeg };
  }
  if (t < SP_HOLD_END) {
    // Hold at full, gentle fade
    const p = (t - SP_IN_END) / (SP_HOLD_END - SP_IN_END);
    return { opacity: 1 - 0.15 * p, scale: 1 - 0.08 * p, rotDeg };
  }
  // Shrink + fade out
  const p = (t - SP_HOLD_END) / (SP_OUT_END - SP_HOLD_END);
  return {
    opacity: Math.max(0, 0.85 * (1 - p)),
    scale: Math.max(0, 0.92 * (1 - p)),
    rotDeg,
  };
}

// ─── Determine warmth of base color ─────────────────────
function isWarmBase(rgb: [number, number, number]): boolean {
  return (rgb[0] - rgb[2]) > 30;
}

// ─── Grid builder ───────────────────────────────────────
interface Disc {
  x: number; y: number;
  phase: number;  // 0–1 fixed offset into cycle
  dur: number;
  flash: boolean;
  sparkle: boolean;
  sparkleAngle: number;
  foreignIdx: number; // -1 = use base palette, 0-2 = index into foreign palette
}

function buildGrid(w: number, h: number, seed: number, fRatio = FOREIGN_RATIO, fCount = 3): Disc[] {
  let s = seed * 7919 + 1337;
  const rand = () => { s = (s * 16807) % 2147483647; return (s & 0x7fffffff) / 0x7fffffff; };

  const cols = Math.ceil(w / STEP) + 1;
  const rows = Math.ceil(h / ROW_H) + 1;
  const out: Disc[] = [];
  for (let r = 0; r < rows; r++) {
    const xOff = (r & 1) * (STEP / 2);
    for (let c = 0; c < cols; c++) {
      if (rand() > DENSITY) continue;
      const flash = rand() < FLASH_RATIO;
      const sparkle = rand() < SPARKLE_RATIO;
      const sparkleAngle = rand() * 360;
      const dur = flash
        ? FLASH_DUR_MIN + rand() * (FLASH_DUR_MAX - FLASH_DUR_MIN)
        : DUR_MIN + rand() * (DUR_MAX - DUR_MIN);
      const phase = rand();
      const foreignIdx = rand() < fRatio
        ? Math.min(fCount - 1, Math.floor(rand() * fCount))
        : -1;
      out.push({ x: c * STEP + xOff, y: r * ROW_H, phase, dur, flash, sparkle, sparkleAngle, foreignIdx });
    }
  }
  return out;
}

// ─── Component ──────────────────────────────────────────
export const PAILLETTE_GOLD_BASE = DEFAULT_BASE;

/** Foreign glint entry — custom off-palette colors for metallic variety. */
export interface ForeignGlint {
  normal: string;
  flash: string;
  sparkle: [number, number, number];
}

// ─── Pale-gold palette (warm brownish-gold + mixed metallics) ──────
export const PALE_GOLD_BASE = '#c4a060';
export const PALE_GOLD_FOREIGN: ForeignGlint[] = [
  { normal: 'rgb(185,190,210)', flash: 'rgb(218,222,238)', sparkle: [236, 238, 248] },  // cool silver
  { normal: 'rgb(205,170,148)', flash: 'rgb(232,200,180)', sparkle: [245, 225, 212] },  // rose gold
  { normal: 'rgb(198,198,205)', flash: 'rgb(228,228,235)', sparkle: [242, 242, 248] },  // platinum
  { normal: 'rgb(178,142,55)',  flash: 'rgb(215,185,100)', sparkle: [235, 218, 158] },  // deep amber
  { normal: 'rgb(212,200,168)', flash: 'rgb(236,228,205)', sparkle: [248, 244, 230] },  // champagne
];
export const PALE_GOLD_FOREIGN_RATIO = 0.08;

export default memo(function PailletteFace({
  width,
  height,
  columnIndex,
  baseColor = DEFAULT_BASE,
  glintIntensity = 1,
  foreignGlints,
  foreignRatio,
}: {
  width: number;
  height: number;
  columnIndex: number;
  baseColor?: string;
  /** Scale factor for glint brightness (0–1). Use <1 for dark bases like charcoal. */
  glintIntensity?: number;
  /** Custom foreign glint palette. Overrides the auto-selected cool/warm palette. */
  foreignGlints?: ForeignGlint[];
  /** Ratio of discs that use foreign glint colors (0–1). Default 0.02. */
  foreignRatio?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fRatio = foreignRatio ?? FOREIGN_RATIO;
  const fCount = foreignGlints ? foreignGlints.length : 3;

  const discs = useMemo(
    () => buildGrid(width, height, columnIndex, fRatio, fCount),
    [width, height, columnIndex, fRatio, fCount],
  );

  const colors = useMemo(() => {
    const rgb = parseHex(baseColor);
    const g = glintIntensity;
    const bright = lightenRGB(rgb, 0.55 * g);
    const superBright = lightenRGB(rgb, 0.82 * g);
    const sparkle = lightenRGB(rgb, 0.95 * g);
    const rawForeign = foreignGlints ?? (isWarmBase(rgb) ? COOL_GLINTS : WARM_GLINTS);
    // Blend foreign colors toward base at low intensity (keeps them subtle on dark bases)
    const blend = (c: number, b: number) => Math.round(b + (c - b) * g);
    const parseRgb = (s: string) => s.match(/\d+/g)!.map(Number) as [number, number, number];
    const foreign = g < 1 ? rawForeign.map(fg => {
      const n = parseRgb(fg.normal);
      const f = parseRgb(fg.flash);
      return {
        normal: `rgb(${blend(n[0],rgb[0])},${blend(n[1],rgb[1])},${blend(n[2],rgb[2])})`,
        flash: `rgb(${blend(f[0],rgb[0])},${blend(f[1],rgb[1])},${blend(f[2],rgb[2])})`,
        sparkle: [blend(fg.sparkle[0],rgb[0]), blend(fg.sparkle[1],rgb[1]), blend(fg.sparkle[2],rgb[2])] as [number, number, number],
      };
    }) : rawForeign;
    return {
      bright: `rgb(${bright[0]},${bright[1]},${bright[2]})`,
      superBright: `rgb(${superBright[0]},${superBright[1]},${superBright[2]})`,
      sparkleR: sparkle[0], sparkleG: sparkle[1], sparkleB: sparkle[2],
      foreign,
    };
  }, [baseColor, glintIntensity, foreignGlints]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let rafId: number;
    const TAU = Math.PI * 2;
    const DEG_TO_RAD = Math.PI / 180;

    const draw = () => {
      const now = performance.now() / 1000;
      // Paint base color directly (avoids GPU compositing artifacts from transparent canvas)
      ctx.globalAlpha = 1;
      ctx.fillStyle = baseColor;
      ctx.fillRect(0, 0, width, height);

      for (let i = 0; i < discs.length; i++) {
        const d = discs[i];
        const t = ((now / d.dur + d.phase) % 1);
        const alpha = glintOpacity(t);

        if (alpha > 0.01) {
          ctx.globalAlpha = alpha;
          if (d.foreignIdx >= 0) {
            const fg = colors.foreign[d.foreignIdx];
            ctx.fillStyle = d.flash ? fg.flash : fg.normal;
          } else {
            ctx.fillStyle = d.flash ? colors.superBright : colors.bright;
          }
          ctx.beginPath();
          ctx.arc(d.x, d.y, DISC_R, 0, TAU);
          ctx.fill();
        }

        // Phase-locked sparkle: cross-star blooms at disc peak brightness
        if (d.sparkle) {
          const sp = sparkleParams(t);
          if (sp.opacity > 0.01 && sp.scale > 0.01) {
            ctx.save();
            ctx.globalAlpha = sp.opacity;
            if (d.foreignIdx >= 0) {
              const fg = colors.foreign[d.foreignIdx];
              ctx.fillStyle = `rgba(${fg.sparkle[0]},${fg.sparkle[1]},${fg.sparkle[2]},${sp.opacity})`;
            } else {
              ctx.fillStyle = `rgba(${colors.sparkleR},${colors.sparkleG},${colors.sparkleB},${sp.opacity})`;
            }
            ctx.translate(d.x, d.y);
            ctx.rotate((d.sparkleAngle + sp.rotDeg) * DEG_TO_RAD);
            const s = SPARKLE_HALF * sp.scale;
            ctx.beginPath();
            ctx.moveTo(STAR_PTS[0][0] * s, STAR_PTS[0][1] * s);
            for (let j = 1; j < STAR_PTS.length; j++) {
              ctx.lineTo(STAR_PTS[j][0] * s, STAR_PTS[j][1] * s);
            }
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
        }
      }

      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(draw);
    };

    draw();  // paint first frame synchronously — no blank-frame ghost on mount
    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [discs, colors, width, height, baseColor]);

  return (
    <div style={{
      position: 'relative',
      width,
      height,
      overflow: 'hidden',
      backgroundColor: baseColor,
    }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ position: 'absolute', top: 0, left: 0, width, height }}
      />
    </div>
  );
});
